import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import LoadingDataDiv from "../common/LoadingDataDiv";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import ScheduleAvailabilityForm from "./ScheduleAvailabilityForm";
import ScheduleAbsenceForm from "./ScheduleAbsenceForm";
import Employee from "../admin/employees/Employee";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const dayFormatter = new Intl.DateTimeFormat("pl-PL", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit"
});

const parseLocalDate = value => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const dateToString = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const todayString = () => dateToString(new Date());

const startOfWeek = value => {
  const date = parseLocalDate(value);
  const weekday = date.getDay() || 7;
  date.setDate(date.getDate() - weekday + 1);
  return date;
};

const endOfWeek = value => {
  const date = startOfWeek(value);
  date.setDate(date.getDate() + 6);
  return date;
};

const startOfMonth = value => {
  const date = parseLocalDate(value);
  return new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
};

const endOfMonth = value => {
  const date = parseLocalDate(value);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0);
};

const resolveRange = (view, anchor, customFrom, customTo) => {
  let from;
  let to;
  if (view === "DAY") {
    from = parseLocalDate(anchor);
    to = parseLocalDate(anchor);
  } else if (view === "WEEK") {
    from = startOfWeek(anchor);
    to = endOfWeek(anchor);
  } else if (view === "BIWEEK") {
    from = startOfWeek(anchor);
    to = new Date(from);
    to.setDate(to.getDate() + 13);
  } else if (view === "MONTH") {
    from = startOfMonth(anchor);
    to = endOfMonth(anchor);
  } else {
    from = parseLocalDate(customFrom);
    to = parseLocalDate(customTo);
  }
  if (!from) from = parseLocalDate(todayString());
  if (!to || to < from) to = new Date(from);
  return { dateFrom: dateToString(from), dateTo: dateToString(to) };
};

const shiftAnchor = (value, view, direction) => {
  const date = parseLocalDate(value);
  if (view === "MONTH") {
    date.setDate(1);
    date.setMonth(date.getMonth() + direction);
  } else if (view === "BIWEEK") date.setDate(date.getDate() + direction * 14);
  else if (view === "WEEK") date.setDate(date.getDate() + direction * 7);
  else date.setDate(date.getDate() + direction);
  return dateToString(date);
};

const buildDateRange = (dateFrom, dateTo) => {
  const from = parseLocalDate(dateFrom);
  const to = parseLocalDate(dateTo);
  const result = [];
  const cursor = new Date(from);
  while (cursor <= to) {
    result.push({ key: dateToString(cursor), label: dayFormatter.format(cursor) });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

const assignmentDate = value => value ? value.slice(0, 10) : "";
const formatTime = value => value ? value.slice(11, 16) : "";
const numericHours = (startsAt, endsAt) => {
  if (!startsAt || !endsAt) return 0;
  return Math.max(0, (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 3600000);
};
const formatHours = value => `${Number(value || 0).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} h`;

const normalizeSearchText = value => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("pl-PL")
  .trim();

const dayOfWeekName = value => {
  const date = parseLocalDate(value);
  return ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"][date.getDay()];
};

const availabilityAppliesOn = (item, date) => {
  if (!item || date < item.dateFrom || date > item.dateTo) return false;
  if (item.recurrence === "WEEKLY") return (item.daysOfWeek ?? []).includes(dayOfWeekName(date));
  return true;
};

const absenceAppliesOn = (item, date) => !!item && date >= item.dateFrom && date <= item.dateTo;
const isWeekend = date => [0, 6].includes(parseLocalDate(date).getDay());

const consecutiveDaysContaining = (workDates, date) => {
  if (!workDates.has(date)) return 0;
  let count = 1;
  let cursor = parseLocalDate(date);
  cursor.setDate(cursor.getDate() - 1);
  while (workDates.has(dateToString(cursor))) {
    count += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  cursor = parseLocalDate(date);
  cursor.setDate(cursor.getDate() + 1);
  while (workDates.has(dateToString(cursor))) {
    count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const getErrorMessage = error => error?.response?.data?.message || error?.response?.data || "Nie udało się wykonać operacji.";

const EmployeeAvailabilitySchedule = () => {
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();
  const requestedSeasonality = searchParams.get("seasonality") === "1";
  const requestedDepartmentId = searchParams.get("departmentId") || "";
  const requestedEmployeeId = Number(searchParams.get("employeeId")) || null;
  const { auth } = useAuth();
  const userRoles = Array.isArray(auth?.roles)
    ? auth.roles
    : typeof auth?.roles === "string"
      ? auth.roles.replace(/[\[\]\s]/g, "").split(",")
      : [];
  const isAdmin = userRoles.includes("ROLE_ADMIN");
  const isManager = userRoles.includes("ROLE_MANAGER") && !isAdmin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(requestedDepartmentId);
  const [employeeFilterText, setEmployeeFilterText] = useState("");
  const [employeeSuggestionsOpen, setEmployeeSuggestionsOpen] = useState(false);
  const [positionFilterId, setPositionFilterId] = useState("");
  const [studentStatusFilter, setStudentStatusFilter] = useState("");
  const [calendarData, setCalendarData] = useState(null);
  const [calendarView, setCalendarView] = useState("WEEK");
  const [calendarAnchor, setCalendarAnchor] = useState(todayString());
  const [customDateFrom, setCustomDateFrom] = useState(todayString());
  const [customDateTo, setCustomDateTo] = useState(todayString());
  const [calendarCell, setCalendarCell] = useState(null);
  const [calendarCellMode, setCalendarCellMode] = useState("MENU");
  const [editedAvailability, setEditedAvailability] = useState(null);
  const [editedAbsence, setEditedAbsence] = useState(null);
  const [seasonalityOpen, setSeasonalityOpen] = useState(requestedSeasonality);
  const [seasonalLoading, setSeasonalLoading] = useState(false);
  const [seasonalApplying, setSeasonalApplying] = useState(false);
  const [seasonalDepartments, setSeasonalDepartments] = useState([]);
  const [seasonalDepartmentId, setSeasonalDepartmentId] = useState(requestedDepartmentId);
  const [seasonalGroup, setSeasonalGroup] = useState(requestedSeasonality ? "ALL" : "RESTRICTED");
  const [seasonalDateFrom, setSeasonalDateFrom] = useState(todayString());
  const [seasonalDateTo, setSeasonalDateTo] = useState(todayString());
  const [seasonalLabel, setSeasonalLabel] = useState("");
  const [seasonalCandidates, setSeasonalCandidates] = useState([]);
  const [seasonalSelectedIds, setSeasonalSelectedIds] = useState([]);
  const [seasonalRequestedEmployeeId, setSeasonalRequestedEmployeeId] = useState(requestedEmployeeId);
  const [seasonalBatches, setSeasonalBatches] = useState([]);
  const [employeeModalId, setEmployeeModalId] = useState(null);

  const visibleRange = useMemo(
    () => resolveRange(calendarView, calendarAnchor, customDateFrom, customDateTo),
    [calendarView, calendarAnchor, customDateFrom, customDateTo]
  );

  const dateRange = useMemo(
    () => buildDateRange(visibleRange.dateFrom, visibleRange.dateTo),
    [visibleRange.dateFrom, visibleRange.dateTo]
  );

  const nonWorkingDayMap = useMemo(() => new Map(
    (calendarData?.nonWorkingDays ?? []).filter(item => item?.date).map(item => [item.date, item])
  ), [calendarData?.nonWorkingDays]);

  const isNonWorkingDate = date => isWeekend(date) || nonWorkingDayMap.has(date);

  const loadCalendar = async () => {
    if (isManager && !departmentId) {
      setCalendarData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/employee-availability-schedules", {
        params: {
          dateFrom: visibleRange.dateFrom,
          dateTo: visibleRange.dateTo,
          ...(departmentId ? { departmentId: Number(departmentId) } : {})
        }
      });
      setCalendarData(response.data);
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadDepartments = async () => {
      try {
        const response = await axiosPrivate.get("/schedule-access/departments");
        if (active) {
          const items = response.data ?? [];
          setDepartments(items);
          if (isManager) {
            setDepartmentId(current => {
              if (current && items.some(item => String(item.id) === String(current))) return current;
              return items.length ? String(items[0].id) : "";
            });
          }
        }
      } catch (error) {
        if (active) toastError("Nie udało się pobrać działów.");
      }
    };
    loadDepartments();
    return () => { active = false; };
  }, [axiosPrivate, isManager]);

  useEffect(() => {
    loadCalendar();
  }, [visibleRange.dateFrom, visibleRange.dateTo, departmentId, isManager]);

  useEffect(() => {
    if (!seasonalityOpen) return;
    let active = true;
    const loadSeasonalDepartments = async () => {
      try {
        const response = await axiosPrivate.get("/seasonal-availability/departments");
        if (!active) return;
        const items = response.data ?? [];
        setSeasonalDepartments(items);
        setSeasonalDepartmentId(current => {
          if (current && items.some(item => String(item.id) === String(current))) return current;
          if (departmentId && items.some(item => String(item.id) === String(departmentId))) return String(departmentId);
          return items.length ? String(items[0].id) : "";
        });
      } catch (error) {
        if (active) toastError(getErrorMessage(error));
      }
    };
    loadSeasonalDepartments();
    return () => { active = false; };
  }, [seasonalityOpen, axiosPrivate, departmentId]);

  useEffect(() => {
    if (!seasonalityOpen || !seasonalDepartmentId) {
      setSeasonalCandidates([]);
      setSeasonalBatches([]);
      return;
    }
    let active = true;
    const loadSeasonalData = async () => {
      setSeasonalLoading(true);
      try {
        const [candidatesResponse, batchesResponse] = await Promise.all([
          axiosPrivate.get("/seasonal-availability/candidates", {
            params: { departmentId: Number(seasonalDepartmentId), group: seasonalGroup }
          }),
          axiosPrivate.get("/seasonal-availability/batches", {
            params: { departmentId: Number(seasonalDepartmentId) }
          })
        ]);
        if (!active) return;
        const candidates = candidatesResponse.data ?? [];
        setSeasonalCandidates(candidates);
        const requestedEmployee = seasonalRequestedEmployeeId == null
          ? null
          : candidates.find(item => String(item.id) === String(seasonalRequestedEmployeeId));
        if (requestedEmployee) {
          setSeasonalSelectedIds([requestedEmployee.id]);
          setSeasonalRequestedEmployeeId(null);
        } else {
          setSeasonalSelectedIds(current => current.filter(id => candidates.some(item => item.id === id)));
        }
        setSeasonalBatches(batchesResponse.data ?? []);
      } catch (error) {
        if (active) toastError(getErrorMessage(error));
      } finally {
        if (active) setSeasonalLoading(false);
      }
    };
    loadSeasonalData();
    return () => { active = false; };
  }, [seasonalityOpen, seasonalDepartmentId, seasonalGroup, axiosPrivate]);

  const employeeOptions = useMemo(
    () => [...(calendarData?.employees ?? [])].sort((left, right) => String(left.fullName || "").localeCompare(String(right.fullName || ""), "pl-PL")),
    [calendarData?.employees]
  );

  const employeeSuggestions = useMemo(() => {
    const query = normalizeSearchText(employeeFilterText);
    const filtered = query
      ? employeeOptions.filter(employee => normalizeSearchText(employee.fullName).includes(query))
      : employeeOptions;
    return filtered.slice(0, 100);
  }, [employeeOptions, employeeFilterText]);

  const positionOptions = useMemo(() => {
    const byId = new Map();
    (calendarData?.employees ?? []).forEach(employee => {
      (employee.positions ?? []).forEach(position => {
        if (departmentId && String(position.departmentId ?? "") !== String(departmentId)) return;
        if (position.id != null) byId.set(String(position.id), position);
      });
    });
    return [...byId.values()].sort((left, right) => String(left.name || left.shortName || "").localeCompare(String(right.name || right.shortName || ""), "pl-PL"));
  }, [calendarData?.employees, departmentId]);

  useEffect(() => {
    if (positionFilterId && !positionOptions.some(item => String(item.id) === String(positionFilterId))) {
      setPositionFilterId("");
    }
  }, [positionFilterId, positionOptions]);

  const rows = useMemo(() => {
    if (!calendarData) return [];
    const byEmployee = new Map();
    (calendarData.employees ?? []).forEach(employee => {
      byEmployee.set(String(employee.id), {
        ...employee,
        byDate: {},
        workDates: new Set(),
        weekHours: 0,
        monthHours: 0,
        maxConsecutiveVisible: 0
      });
    });

    (calendarData.workloadAssignments ?? []).forEach(assignment => {
      if (assignment.employeeId == null || assignment.status === "CANCELLED") return;
      const row = byEmployee.get(String(assignment.employeeId));
      if (!row) return;
      const date = assignmentDate(assignment.startsAt);
      if (!row.byDate[date]) row.byDate[date] = [];
      row.byDate[date].push(assignment);
      row.workDates.add(date);
    });

    const summaryAnchor = calendarView === "CUSTOM" ? visibleRange.dateFrom : calendarAnchor;
    const weekFrom = dateToString(startOfWeek(summaryAnchor));
    const weekTo = dateToString(endOfWeek(summaryAnchor));
    const monthFrom = dateToString(startOfMonth(summaryAnchor));
    const monthTo = dateToString(endOfMonth(summaryAnchor));

    byEmployee.forEach(row => {
      Object.entries(row.byDate).forEach(([date, assignments]) => {
        const hours = assignments.reduce((sum, assignment) => sum + numericHours(assignment.startsAt, assignment.endsAt), 0);
        if (date >= weekFrom && date <= weekTo) row.weekHours += hours;
        if (date >= monthFrom && date <= monthTo) row.monthHours += hours;
      });
      row.maxConsecutiveVisible = dateRange.reduce(
        (max, day) => Math.max(max, consecutiveDaysContaining(row.workDates, day.key)),
        0
      );
    });

    return [...byEmployee.values()].filter(row => {
      const employeeQuery = normalizeSearchText(employeeFilterText);
      if (employeeQuery) {
        const employeeName = normalizeSearchText(row.fullName);
        const terms = employeeQuery.split(/\s+/).filter(Boolean);
        if (!terms.every(term => employeeName.includes(term))) return false;
      }
      if (positionFilterId && !(row.positions ?? []).some(position => String(position.id) === String(positionFilterId))) return false;

      if (studentStatusFilter === "PUPIL" && !row.pupil) return false;
      if (studentStatusFilter === "STUDENT" && !row.student) return false;
      if (studentStatusFilter === "PUPIL_OR_STUDENT" && !row.pupil && !row.student) return false;
      if (studentStatusFilter === "NONE" && (row.pupil || row.student)) return false;

      return true;
    });
  }, [calendarData, calendarView, calendarAnchor, visibleRange.dateFrom, dateRange, employeeFilterText, positionFilterId, studentStatusFilter]);

  const availabilities = calendarData?.availabilities ?? [];
  const absences = calendarData?.absences ?? [];

  const openCalendarCell = (row, date) => {
    setCalendarCell({ employeeId: row.id, name: row.fullName, date, row });
    setCalendarCellMode("MENU");
    setEditedAvailability(null);
    setEditedAbsence(null);
  };

  const closeCalendarCell = () => {
    if (saving) return;
    setCalendarCell(null);
    setCalendarCellMode("MENU");
    setEditedAvailability(null);
    setEditedAbsence(null);
  };

  const openAvailabilityForm = item => {
    if (item?.entrySource === "SEASONAL") {
      openSeasonality();
      return;
    }
    setEditedAvailability(item || null);
    setCalendarCellMode("AVAILABILITY");
  };

  const openAbsenceForm = item => {
    setEditedAbsence(item || null);
    setCalendarCellMode("ABSENCE");
  };

  const saveAvailability = async (availability, payload) => {
    setSaving(true);
    try {
      if (availability?.id) await axiosPrivate.put(`/employee-availabilities/${availability.id}`, payload);
      else await axiosPrivate.post("/employee-availabilities", payload);
      toastSuccess(availability?.id ? "Zapisano dostępność." : "Dodano dostępność.");
      await loadCalendar();
      return true;
    } catch (error) {
      toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteAvailability = async id => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-availabilities/${id}`);
      toastSuccess("Usunięto wpis dostępności.");
      await loadCalendar();
      return true;
    } catch (error) {
      toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAbsence = async (absence, payload) => {
    setSaving(true);
    try {
      if (absence?.id) await axiosPrivate.put(`/employee-absences/${absence.id}`, payload);
      else await axiosPrivate.post("/employee-absences", payload);
      toastSuccess(absence?.id ? "Zapisano nieobecność." : "Dodano nieobecność.");
      await loadCalendar();
      return true;
    } catch (error) {
      toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteAbsence = async id => {
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-absences/${id}`);
      toastSuccess("Usunięto nieobecność.");
      await loadCalendar();
      return true;
    } catch (error) {
      toastError(getErrorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openSeasonality = () => {
    setSeasonalDateFrom(visibleRange.dateFrom);
    setSeasonalDateTo(visibleRange.dateTo);
    setSeasonalSelectedIds([]);
    setSeasonalityOpen(true);
  };

  const closeSeasonality = () => {
    if (seasonalApplying) return;
    setSeasonalityOpen(false);
    setSeasonalSelectedIds([]);
  };

  const toggleSeasonalEmployee = employeeId => {
    setSeasonalSelectedIds(current => current.includes(employeeId)
      ? current.filter(id => id !== employeeId)
      : [...current, employeeId]);
  };

  const applySeasonality = async () => {
    if (!seasonalDepartmentId) {
      toastError("Wybierz dział.");
      return;
    }
    if (!seasonalDateFrom || !seasonalDateTo || seasonalDateTo < seasonalDateFrom) {
      toastError("Podaj prawidłowy okres sezonu.");
      return;
    }
    if (seasonalSelectedIds.length === 0) {
      toastError("Wybierz co najmniej jednego pracownika.");
      return;
    }
    setSeasonalApplying(true);
    try {
      await axiosPrivate.post("/seasonal-availability/batches", {
        departmentId: Number(seasonalDepartmentId),
        dateFrom: seasonalDateFrom,
        dateTo: seasonalDateTo,
        label: seasonalLabel,
        employeeIds: seasonalSelectedIds
      });
      toastSuccess(`Włączono pełną dostępność sezonową dla ${seasonalSelectedIds.length} pracowników.`);
      setSeasonalSelectedIds([]);
      setSeasonalLabel("");
      const batchesResponse = await axiosPrivate.get("/seasonal-availability/batches", {
        params: { departmentId: Number(seasonalDepartmentId) }
      });
      setSeasonalBatches(batchesResponse.data ?? []);
      await loadCalendar();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSeasonalApplying(false);
    }
  };

  const removeSeasonality = async batch => {
    if (!window.confirm(`Usunąć sezonową pełną dostępność „${batch.label || "bez nazwy"}” dla ${batch.employeeCount} pracowników?`)) return;
    setSeasonalApplying(true);
    try {
      await axiosPrivate.delete(`/seasonal-availability/batches/${batch.batchId}`);
      toastSuccess("Usunięto zmianę sezonową. Obowiązują ponownie podstawowe zasady dostępności pracowników.");
      setSeasonalBatches(current => current.filter(item => item.batchId !== batch.batchId));
      await loadCalendar();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSeasonalApplying(false);
    }
  };

  const cellAvailabilities = calendarCell
    ? availabilities.filter(item => item.EID === calendarCell.employeeId && availabilityAppliesOn(item, calendarCell.date))
    : [];

  const cellAbsences = calendarCell
    ? absences.filter(item => item.EID === calendarCell.employeeId && absenceAppliesOn(item, calendarCell.date))
    : [];

  const cellShifts = calendarCell?.row?.byDate?.[calendarCell.date] ?? [];

  return (
    <div id="layoutSidenav_content" className="availability-calendar-page">
      <div className="app-component-card-header shadow availability-calendar-header">
        <div>
          <div className="availability-calendar-title">Grafik dostępności pracowników</div>
          <div className="availability-calendar-subtitle">Dostępność, nieobecności i zaplanowane godziny w jednym kalendarzu.</div>
        </div>
      </div>

      <div className="container-fluid px-4">
        <div className="schedule-section availability-calendar-section">
          <div className="availability-calendar-controls">
            <div className="schedule-calendar-toolbar availability-calendar-toolbar">
            <div className="schedule-calendar-toolbar-nav">
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(shiftAnchor(calendarAnchor, calendarView, -1))} disabled={calendarView === "CUSTOM"}>‹</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(todayString())} disabled={calendarView === "CUSTOM"}>Dzisiaj</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setCalendarAnchor(shiftAnchor(calendarAnchor, calendarView, 1))} disabled={calendarView === "CUSTOM"}>›</Button>
            </div>

            <Form.Select value={calendarView} onChange={event => setCalendarView(event.target.value)}>
              <option value="DAY">Dzień</option>
              <option value="WEEK">Tydzień</option>
              <option value="BIWEEK">2 tygodnie</option>
              <option value="MONTH">Miesiąc</option>
              <option value="CUSTOM">Dowolny zakres</option>
            </Form.Select>

            {calendarView === "CUSTOM" ? (
              <div className="schedule-calendar-custom-range">
                <Form.Control type="date" value={customDateFrom} onChange={event => setCustomDateFrom(event.target.value)} />
                <span>–</span>
                <Form.Control type="date" min={customDateFrom} value={customDateTo} onChange={event => setCustomDateTo(event.target.value)} />
              </div>
            ) : calendarView === "MONTH" ? (
              <Form.Control
                type="month"
                value={calendarAnchor.slice(0, 7)}
                onChange={event => event.target.value && setCalendarAnchor(`${event.target.value}-01`)}
              />
            ) : (
              <Form.Control type="date" value={calendarAnchor} onChange={event => setCalendarAnchor(event.target.value)} />
            )}

            <Button variant="outline-primary" size="sm" onClick={openSeasonality}>Sterowanie sezonowością</Button>

            <span className="schedule-calendar-visible-range">{visibleRange.dateFrom} – {visibleRange.dateTo}</span>
          </div>

          <div className="availability-calendar-filters">
            <Form.Group className="availability-calendar-filter availability-calendar-employee-filter">
              <Form.Label>Pracownik</Form.Label>
              <div className="availability-calendar-employee-input-wrap">
                <Form.Control
                  type="search"
                  value={employeeFilterText}
                  placeholder="Wpisz imię lub nazwisko"
                  autoComplete="off"
                  onFocus={() => setEmployeeSuggestionsOpen(true)}
                  onBlur={() => setEmployeeSuggestionsOpen(false)}
                  onChange={event => {
                    setEmployeeFilterText(event.target.value);
                    setEmployeeSuggestionsOpen(true);
                  }}
                />
                {employeeSuggestionsOpen && (
                  <div className="availability-calendar-employee-options">
                    {employeeSuggestions.length > 0 ? employeeSuggestions.map(employee => (
                      <button
                        key={employee.id}
                        type="button"
                        className="availability-calendar-employee-option"
                        onMouseDown={event => {
                          event.preventDefault();
                          setEmployeeFilterText(employee.fullName);
                          setEmployeeSuggestionsOpen(false);
                        }}
                      >
                        {employee.fullName}
                      </button>
                    )) : (
                      <div className="availability-calendar-employee-option-empty">Brak pasujących pracowników</div>
                    )}
                  </div>
                )}
              </div>
            </Form.Group>

            <Form.Group className="availability-calendar-filter">
              <Form.Label>Dział</Form.Label>
              <Form.Select className="availability-calendar-department" value={departmentId} onChange={event => {
                setDepartmentId(event.target.value);
                setEmployeeFilterText("");
                setPositionFilterId("");
              }}>
                {isAdmin && <option value="">Wszystkie działy</option>}
                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group className="availability-calendar-filter">
              <Form.Label>Stanowisko</Form.Label>
              <Form.Select value={positionFilterId} onChange={event => setPositionFilterId(event.target.value)}>
                <option value="">Wszystkie stanowiska</option>
                {positionOptions.map(position => <option key={position.id} value={position.id}>{position.name || position.shortName}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group className="availability-calendar-filter">
              <Form.Label>Status uczeń / student</Form.Label>
              <Form.Select value={studentStatusFilter} onChange={event => setStudentStatusFilter(event.target.value)}>
                <option value="">Wszystkie statusy</option>
                <option value="PUPIL">Uczeń</option>
                <option value="STUDENT">Student</option>
                <option value="PUPIL_OR_STUDENT">Uczeń lub student</option>
                <option value="NONE">Bez statusu ucznia/studenta</option>
              </Form.Select>
            </Form.Group>
            </div>
          </div>

          <div className="availability-calendar-legend">
            <span className="availability-legend-item availability-legend-available">Dostępny</span>
            <span className="availability-legend-item availability-legend-preferred">Preferowany</span>
            <span className="availability-legend-item availability-legend-unavailable">Niedostępny</span>
            <span className="availability-legend-item availability-legend-absence">Nieobecność</span>
            <span className="availability-legend-item availability-legend-shift">Zaplanowana zmiana</span>
            <span className="availability-legend-item availability-legend-non-working">Dzień wolny od pracy</span>
          </div>

          {loading ? <LoadingDataDiv /> : rows.length === 0 ? (
            <div className="schedule-empty">Brak pracowników dla wybranych kryteriów.</div>
          ) : (
            <div className="schedule-calendar-wrapper">
              <table className="schedule-calendar-table availability-calendar-table">
                <thead>
                  <tr>
                    <th className="schedule-calendar-employee-column">Pracownik</th>
                    <th className="availability-calendar-student-column">Uczeń / student</th>
                    <th className="schedule-calendar-hours-column">Godziny</th>
                    {dateRange.map(day => {
                      const nonWorkingDay = nonWorkingDayMap.get(day.key);
                      return (
                        <th
                          key={day.key}
                          className={nonWorkingDay ? "schedule-calendar-day-non-working" : ""}
                          title={nonWorkingDay ? nonWorkingDay.name : undefined}
                        >
                          <span>{day.label}</span>
                          {nonWorkingDay && <small className="schedule-calendar-holiday-label">{nonWorkingDay.shortName || nonWorkingDay.name}</small>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const warningThreshold = calendarData?.consecutiveDaysWarningThreshold ?? 5;
                    const maximumConsecutive = row.scheduleMaxConsecutiveWorkDays ?? calendarData?.defaultMaxConsecutiveWorkDays ?? 7;
                    const maxWeeklyHours = Number(row.scheduleMaxWeeklyHours || 0);
                    const minWeeklyHours = Number(row.scheduleMinWeeklyHours || 0);
                    const weeklyHoursClass = maxWeeklyHours > 0 && row.weekHours > maxWeeklyHours
                      ? "availability-hours-critical"
                      : maxWeeklyHours > 0 && row.weekHours >= maxWeeklyHours * 0.9
                        ? "availability-hours-warning"
                        : "";
                    const rowAlertClass = row.maxConsecutiveVisible >= maximumConsecutive
                      ? "schedule-calendar-employee-critical"
                      : row.maxConsecutiveVisible >= warningThreshold
                        ? "schedule-calendar-employee-warning"
                        : "";

                    return (
                      <tr key={row.id}>
                        <th className={`schedule-calendar-employee-column ${rowAlertClass}`}>
                          <button
                            type="button"
                            className="availability-calendar-employee-link"
                            onClick={() => setEmployeeModalId(row.id)}
                          >
                            {row.fullName}
                          </button>
                          <div className="availability-calendar-person-meta">
                            {row.availableOnlyWeekends && <span>Tylko weekendy</span>}
                            {row.availableOnlyWorkingDays && <span>Tylko dni robocze</span>}
                          </div>
                          {row.maxConsecutiveVisible >= warningThreshold && <span className="schedule-calendar-streak-badge">{row.maxConsecutiveVisible} dni z rzędu</span>}
                        </th>
                        <td className="availability-calendar-student-column">
                          <div className="availability-calendar-student-status">
                            {row.pupil && <span className="availability-calendar-student-badge">Uczeń</span>}
                            {row.student && <span className="availability-calendar-student-badge">Student</span>}
                            {!row.pupil && !row.student && <span className="availability-calendar-student-none">Nie</span>}
                          </div>
                        </td>
                        <td className={`schedule-calendar-hours-column ${weeklyHoursClass}`}>
                          <span><b>Tydz.:</b> {formatHours(row.weekHours)}</span>
                          <span><b>Mies.:</b> {formatHours(row.monthHours)}</span>
                          {(minWeeklyHours > 0 || maxWeeklyHours > 0) && (
                            <small>Norma: {minWeeklyHours > 0 ? minWeeklyHours : "–"}–{maxWeeklyHours > 0 ? maxWeeklyHours : "–"} h</small>
                          )}
                        </td>
                        {dateRange.map(day => {
                          const dayAvailabilities = availabilities.filter(item => item.EID === row.id && availabilityAppliesOn(item, day.key));
                          const dayAbsences = absences.filter(item => item.EID === row.id && absenceAppliesOn(item, day.key));
                          const shifts = row.byDate[day.key] ?? [];
                          const dailyHours = shifts.reduce((sum, item) => sum + numericHours(item.startsAt, item.endsAt), 0);
                          const streak = consecutiveDaysContaining(row.workDates, day.key);
                          const hasUnavailable = dayAvailabilities.some(item => item.type === "UNAVAILABLE");
                          const hasPreferred = dayAvailabilities.some(item => item.type === "PREFERRED");
                          const hasAvailable = dayAvailabilities.some(item => item.type === "AVAILABLE");
                          const hasSeasonalAvailable = dayAvailabilities.some(item => item.type === "AVAILABLE" && item.entrySource === "SEASONAL");
                          const dayIsNonWorking = isNonWorkingDate(day.key);
                          const definedNonWorkingDay = nonWorkingDayMap.get(day.key);
                          const baselineUnavailable = !hasSeasonalAvailable && ((row.availableOnlyWeekends && !dayIsNonWorking) || (row.availableOnlyWorkingDays && dayIsNonWorking));
                          const cellClasses = [
                            shifts.length ? "schedule-calendar-cell-with-shift" : "",
                            dayAbsences.length ? "schedule-calendar-cell-absence" : "",
                            hasUnavailable || baselineUnavailable ? "schedule-calendar-cell-unavailable" : "",
                            hasPreferred ? "schedule-calendar-cell-preferred" : "",
                            hasAvailable ? "availability-calendar-cell-available" : "",
                            definedNonWorkingDay ? "schedule-calendar-cell-non-working" : "",
                            streak >= maximumConsecutive ? "schedule-calendar-cell-critical" : streak >= warningThreshold ? "schedule-calendar-cell-warning" : "",
                            "schedule-calendar-cell-editable"
                          ].filter(Boolean).join(" ");

                          return (
                            <td key={day.key} className={cellClasses} onClick={() => openCalendarCell(row, day.key)} title={definedNonWorkingDay?.name}>
                              {definedNonWorkingDay && (
                                <div className="schedule-calendar-holiday-cell-label">{definedNonWorkingDay.shortName || definedNonWorkingDay.name}</div>
                              )}
                              {baselineUnavailable && dayAvailabilities.length === 0 && (
                                <div className="availability-calendar-baseline-badge">{row.availableOnlyWeekends ? "Tylko weekendy" : "Tylko dni robocze"}</div>
                              )}
                              {dayAbsences.map(item => (
                                <button
                                  key={`absence-${item.id}`}
                                  type="button"
                                  className="schedule-calendar-absence-badge"
                                  style={item.absenceType?.color ? { borderLeftColor: item.absenceType.color } : undefined}
                                  onClick={event => {
                                    event.stopPropagation();
                                    setCalendarCell({ employeeId: row.id, name: row.fullName, date: day.key, row });
                                    openAbsenceForm(item);
                                  }}
                                >
                                  {item.absenceType?.shortName || item.absenceType?.name || "Nieobecność"}
                                </button>
                              ))}
                              {dayAvailabilities.map(item => (
                                <button
                                  key={`availability-${item.id}`}
                                  type="button"
                                  className={`schedule-calendar-availability-badge schedule-calendar-availability-${String(item.type || "AVAILABLE").toLowerCase()}${item.entrySource === "SEASONAL" ? " schedule-calendar-availability-seasonal" : ""}`}
                                  onClick={event => {
                                    event.stopPropagation();
                                    if (item.entrySource === "SEASONAL") {
                                      openSeasonality();
                                      return;
                                    }
                                    setCalendarCell({ employeeId: row.id, name: row.fullName, date: day.key, row });
                                    openAvailabilityForm(item);
                                  }}
                                >
                                  {item.entrySource === "SEASONAL" ? "Sezonowo dostępny" : item.type === "UNAVAILABLE" ? "Niedostępny" : item.type === "PREFERRED" ? "Preferowany" : "Dostępny"}
                                  {item.entrySource === "SEASONAL" && item.seasonalLabel && <span>{item.seasonalLabel}</span>}
                                  {item.entrySource !== "SEASONAL" && (item.timeFrom || item.timeTo) && <span>{item.timeFrom?.slice(0, 5) || ""}–{item.timeTo?.slice(0, 5) || ""}</span>}
                                </button>
                              ))}
                              {shifts.map(item => (
                                <div key={`shift-${item.id}`} className="availability-calendar-shift-badge">
                                  <strong>{formatTime(item.startsAt)}–{formatTime(item.endsAt)}</strong>
                                  <span>{item.positionName}</span>
                                </div>
                              ))}
                              <div className="schedule-calendar-cell-footer">
                                {dailyHours > 0 && <span>{formatHours(dailyHours)}</span>}
                                {streak >= warningThreshold && <span>{streak} dni z rzędu</span>}
                                <span className="schedule-calendar-cell-add">+</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={employeeModalId != null}
        onHide={() => setEmployeeModalId(null)}
        size="xl"
        centered
        scrollable
        className="schedule-employee-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Kartoteka pracownika</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {employeeModalId != null && (
            <Employee
              employeeId={employeeModalId}
              embedded
              onEmbeddedClose={() => setEmployeeModalId(null)}
              onEmbeddedSaved={loadCalendar}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!calendarCell} onHide={closeCalendarCell} centered scrollable backdrop="static" className="schedule-calendar-cell-modal">
        <Modal.Header closeButton>
          <Modal.Title>{calendarCell?.name} · {calendarCell?.date}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {calendarCellMode === "MENU" && (
            <div className="schedule-calendar-cell-menu">
              <div className="schedule-calendar-cell-actions">
                <Button variant="primary" onClick={() => openAvailabilityForm(null)}>Dodaj dostępność / niedostępność</Button>
                <Button variant="outline-primary" onClick={() => openAbsenceForm(null)}>Dodaj nieobecność</Button>
              </div>

              <div className="schedule-calendar-cell-existing">
                <h6>Dostępność</h6>
                {cellAvailabilities.length === 0 ? <div className="schedule-calendar-cell-empty">Brak dodatkowych wpisów.</div> : cellAvailabilities.map(item => (
                  <button key={item.id} type="button" className="schedule-calendar-cell-existing-item" onClick={() => item.entrySource === "SEASONAL" ? openSeasonality() : openAvailabilityForm(item)}>
                    <strong>{item.entrySource === "SEASONAL" ? "Sezonowa pełna dostępność" : item.type === "UNAVAILABLE" ? "Niedostępny" : item.type === "PREFERRED" ? "Preferowana dostępność" : "Dostępny"}</strong>
                    <span>{item.entrySource === "SEASONAL" ? `${item.dateFrom}–${item.dateTo}` : `${item.timeFrom ? item.timeFrom.slice(0, 5) : "cały dzień"}${item.timeTo ? `–${item.timeTo.slice(0, 5)}` : ""}`}</span>
                    {item.comments && <small>{item.comments}</small>}
                  </button>
                ))}
              </div>

              <div className="schedule-calendar-cell-existing">
                <h6>Nieobecności</h6>
                {cellAbsences.length === 0 ? <div className="schedule-calendar-cell-empty">Brak nieobecności.</div> : cellAbsences.map(item => (
                  <button key={item.id} type="button" className="schedule-calendar-cell-existing-item" onClick={() => openAbsenceForm(item)}>
                    <strong>{item.absenceType?.name || "Nieobecność"}</strong>
                    <span>{item.dateFrom}–{item.dateTo}</span>
                    {item.comments && <small>{item.comments}</small>}
                  </button>
                ))}
              </div>

              <div className="schedule-calendar-cell-existing">
                <h6>Zaplanowane zmiany</h6>
                {cellShifts.length === 0 ? <div className="schedule-calendar-cell-empty">Brak zmian.</div> : cellShifts.map(item => (
                  <div key={item.id} className="schedule-calendar-cell-existing-item availability-calendar-existing-shift">
                    <strong>{formatTime(item.startsAt)}–{formatTime(item.endsAt)}</strong>
                    <span>{item.positionName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {calendarCellMode === "AVAILABILITY" && calendarCell && (
            <ScheduleAvailabilityForm
              employeeId={calendarCell.employeeId}
              date={calendarCell.date}
              availability={editedAvailability}
              onSave={saveAvailability}
              onDelete={deleteAvailability}
              onCancel={() => { setCalendarCellMode("MENU"); setEditedAvailability(null); }}
              saving={saving}
            />
          )}

          {calendarCellMode === "ABSENCE" && calendarCell && (
            <ScheduleAbsenceForm
              employeeId={calendarCell.employeeId}
              date={calendarCell.date}
              absence={editedAbsence}
              absenceTypes={calendarData?.absenceTypes ?? []}
              supervisors={calendarData?.supervisors ?? []}
              onSave={saveAbsence}
              onDelete={deleteAbsence}
              onCancel={() => { setCalendarCellMode("MENU"); setEditedAbsence(null); }}
              saving={saving}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={seasonalityOpen} onHide={closeSeasonality} centered size="xl" scrollable backdrop="static" className="seasonality-modal">
        <Modal.Header closeButton>
          <Modal.Title>Sterowanie sezonowością</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="seasonality-intro">
            Tymczasowa pełna dostępność nadpisuje podstawowe ograniczenie „tylko weekendy” lub „tylko dni robocze” wyłącznie w podanym okresie. Nieobecności i ręcznie wpisana niedostępność nadal mają pierwszeństwo.
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Form.Label>Dział</Form.Label>
              <Form.Select value={seasonalDepartmentId} onChange={event => { setSeasonalDepartmentId(event.target.value); setSeasonalSelectedIds([]); }}>
                <option value="">Wybierz dział</option>
                {seasonalDepartments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label>Grupa bazowa</Form.Label>
              <Form.Select value={seasonalGroup} onChange={event => { setSeasonalGroup(event.target.value); setSeasonalSelectedIds([]); }}>
                <option value="RESTRICTED">Wszyscy z ograniczeniem</option>
                <option value="WEEKENDS_ONLY">Tylko weekendowi</option>
                <option value="WORKING_DAYS_ONLY">Tylko dni robocze</option>
                <option value="ALL">Wszyscy pracownicy działu</option>
              </Form.Select>
            </div>
            <div className="col-md-2">
              <Form.Label>Od</Form.Label>
              <Form.Control type="date" value={seasonalDateFrom} onChange={event => setSeasonalDateFrom(event.target.value)} />
            </div>
            <div className="col-md-2">
              <Form.Label>Do</Form.Label>
              <Form.Control type="date" min={seasonalDateFrom} value={seasonalDateTo} onChange={event => setSeasonalDateTo(event.target.value)} />
            </div>
            <div className="col-md-2">
              <Form.Label>Nazwa sezonu</Form.Label>
              <Form.Control value={seasonalLabel} maxLength={120} placeholder="np. Sezon letni 2026" onChange={event => setSeasonalLabel(event.target.value)} />
            </div>
          </div>

          <div className="seasonality-columns">
            <div className="seasonality-panel">
              <div className="seasonality-panel-header">
                <div>
                  <strong>Pracownicy</strong>
                  <span>{seasonalCandidates.length} w grupie · zaznaczono {seasonalSelectedIds.length}</span>
                </div>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" disabled={!seasonalCandidates.length} onClick={() => setSeasonalSelectedIds(seasonalCandidates.map(item => item.id))}>Zaznacz wszystkich</Button>
                  <Button size="sm" variant="outline-secondary" disabled={!seasonalSelectedIds.length} onClick={() => setSeasonalSelectedIds([])}>Wyczyść</Button>
                </div>
              </div>
              <div className="seasonality-employee-list">
                {seasonalLoading ? <LoadingDataDiv /> : seasonalCandidates.length === 0 ? (
                  <div className="schedule-empty">Brak pracowników w wybranej grupie.</div>
                ) : seasonalCandidates.map(employee => (
                  <label key={employee.id} className="seasonality-employee-row">
                    <input type="checkbox" checked={seasonalSelectedIds.includes(employee.id)} onChange={() => toggleSeasonalEmployee(employee.id)} />
                    <span className="seasonality-employee-name">{employee.fullName}</span>
                    <span className="seasonality-base-mode">{employee.baseAvailabilityMode}</span>
                  </label>
                ))}
              </div>
              <div className="seasonality-apply-row">
                <Button variant="primary" disabled={seasonalApplying || !seasonalSelectedIds.length || !seasonalDepartmentId} onClick={applySeasonality}>
                  {seasonalApplying ? "Zapisywanie…" : `Włącz pełną dostępność (${seasonalSelectedIds.length})`}
                </Button>
              </div>
            </div>

            <div className="seasonality-panel">
              <div className="seasonality-panel-header">
                <div>
                  <strong>Aktywne i historyczne zmiany sezonowe</strong>
                  <span>Usunięcie przywraca podstawowe zasady dostępności.</span>
                </div>
              </div>
              <div className="seasonality-batch-list">
                {seasonalLoading ? <LoadingDataDiv /> : seasonalBatches.length === 0 ? (
                  <div className="schedule-empty">Brak zmian sezonowych dla działu.</div>
                ) : seasonalBatches.map(batch => (
                  <div key={batch.batchId} className="seasonality-batch-row">
                    <div>
                      <strong>{batch.label || "Sezon bez nazwy"}</strong>
                      <span>{batch.dateFrom} – {batch.dateTo}</span>
                      <small>{batch.employeeCount} pracowników{batch.employees?.length ? `: ${batch.employees.map(item => item.fullName).join(", ")}` : ""}</small>
                    </div>
                    <Button size="sm" variant="outline-danger" disabled={seasonalApplying} onClick={() => removeSeasonality(batch)}>Usuń</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={seasonalApplying} onClick={closeSeasonality}>Zamknij</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeAvailabilitySchedule;
